import type { AssistantActionPreviewResponse, AssistantCapability } from './types';

export type AssistantCapabilityBrowserPreviewState = {
  status: 'idle' | 'loading' | 'ready' | 'error';
  preview?: AssistantActionPreviewResponse | null;
  errorMessage?: string | null;
};

export type AssistantCapabilityBrowserActionState =
  | {
      kind: 'ask';
      label: 'Ask';
      disabled: false;
      helperText: string;
    }
  | {
      kind: 'checking';
      label: 'Checking…';
      disabled: true;
      helperText: string;
    }
  | {
      kind: 'run';
      label: 'Run';
      disabled: false;
      helperText: string;
    }
  | {
      kind: 'open_page';
      label: 'Open page';
      disabled: false;
      helperText: string;
    }
  | {
      kind: 'unavailable';
      label: 'Unavailable';
      disabled: true;
      helperText: string;
    };

export const resolveAssistantCapabilityBrowserActionState = (
  capability: AssistantCapability,
  previewState?: AssistantCapabilityBrowserPreviewState,
): AssistantCapabilityBrowserActionState => {
  if (capability.type !== 'action') {
    return {
      kind: 'ask',
      label: 'Ask',
      disabled: false,
      helperText: capability.prompt || capability.rationale,
    };
  }

  if (!previewState || previewState.status === 'idle' || previewState.status === 'loading') {
    return {
      kind: 'checking',
      label: 'Checking…',
      disabled: true,
      helperText: 'Verifying whether this action can be run from the current route and persona.',
    };
  }

  if (previewState.status === 'error' || !previewState.preview) {
    return {
      kind: 'unavailable',
      label: 'Unavailable',
      disabled: true,
      helperText: previewState.errorMessage || 'This action could not be verified right now.',
    };
  }

  const preview = previewState.preview;
  const missingInputs = Array.isArray(preview.missingInputs) ? preview.missingInputs : [];

  if (!preview.allowed) {
    return {
      kind: 'unavailable',
      label: 'Unavailable',
      disabled: true,
      helperText: preview.message || 'This action is not available in the current persona context.',
    };
  }

  if (missingInputs.length > 0 && preview.targetRoute) {
    return {
      kind: 'open_page',
      label: 'Open page',
      disabled: false,
      helperText: `Needs: ${missingInputs.join(', ')}`,
    };
  }

  return {
    kind: 'run',
    label: 'Run',
    disabled: false,
    helperText: preview.message || preview.summary || capability.rationale,
  };
};
